// OIDC sign-in callback (unified-auth migration, Phase 4).
//
// After @logto/react completes the Authorization Code + PKCE redirect, this page
// gets the Logto ACCESS TOKEN for the IOSentra API resource and exchanges it at
// POST /auth/session for an IOSentra session, then routes into the app. Rendered
// only in the oidc flow (App.tsx intercepts /callback before the auth gate).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHandleSignInCallback, useLogto } from '@logto/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { API_RESOURCE } from '@/auth/logto-config';
import { IOSentraLogo } from '@/components/IOSentraLogo';
import { IOSentraWordmark } from '@/components/IOSentraWordmark';

export function CallbackPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { getAccessToken } = useLogto();
  const { exchangeLogtoToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchanged = useRef(false);

  // 1) Let the SDK finish the PKCE exchange (reads ?code=&state= from the URL).
  const { isLoading, isAuthenticated, error: cbError } = useHandleSignInCallback();

  useEffect(() => {
    if (cbError) setError(t('callback.errAuth', 'Sign-in could not be completed.'));
  }, [cbError, t]);

  // 2) Once authenticated with Logto, get the access token for the IOSentra API
  //    resource and exchange it for the IOSentra session, then go home.
  useEffect(() => {
    if (!isAuthenticated || exchanged.current) return;
    exchanged.current = true;
    (async () => {
      try {
        const token = await getAccessToken(API_RESOURCE);
        if (!token) throw new Error('no_access_token');
        await exchangeLogtoToken(token);
        navigate('/', { replace: true });
      } catch (e: unknown) {
        const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(
          code === 'no_customer_membership'
            ? t('callback.errNoMembership', 'Your account is not linked to any customer. Contact your administrator.')
            : t('callback.errExchange', 'Could not establish your IOSentra session. Please try again.'),
        );
      }
    })();
  }, [isAuthenticated, getAccessToken, exchangeLogtoToken, navigate, t]);

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-surface/80 p-8 text-center shadow-2xl backdrop-blur">
        <div className="mb-6 flex items-center justify-center gap-3">
          <IOSentraLogo className="h-9 w-9" />
          <IOSentraWordmark className="h-5" />
        </div>
        {error ? (
          <>
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-4 text-sm font-medium text-primary hover:underline"
            >
              {t('callback.backToLogin', 'Back to sign in')}
            </button>
          </>
        ) : (
          <p className="text-sm text-muted">
            {isLoading ? t('callback.completing', 'Completing sign-in…') : t('callback.finishing', 'Finishing up…')}
          </p>
        )}
      </div>
    </div>
  );
}
